/**
 * Shared notification chime (same "gentle chime" as the header bell), playable
 * from anywhere — e.g. when Victory Live replies while the bell popup is closed.
 * Browsers only allow audio after a user gesture, so the context is unlocked on
 * the first click/keypress.
 */
let audioContext: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContext) audioContext = new AudioContextClass();
    return audioContext;
  } catch {
    return null;
  }
};

const beep = (frequency: number, duration: number, volume: number) => {
  const ctx = getContext();
  if (!ctx) return;
  try {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    if (ctx.state === 'suspended') void ctx.resume();
    oscillator.start();
    oscillator.stop(ctx.currentTime + duration);
  } catch {
    // audio is best-effort
  }
};

const playChimeNotes = () => {
  beep(523, 1.2, 0.22);
  window.setTimeout(() => beep(659, 1.2, 0.22), 400);
  window.setTimeout(() => beep(784, 1.5, 0.22), 800);
};

/** A chime the browser refused (no click or key press on this page yet). It plays on the next one. */
let chimePending = false;

/**
 * Plays the chime. Browsers keep audio locked until the person has interacted with the page, so when
 * it is still locked the chime is held and played on their next click or key press, instead of
 * being lost. Returns true when it played right away.
 */
export function playNotificationChime(): boolean {
  const ctx = getContext();
  if (!ctx) return false;
  if (ctx.state === 'running') {
    playChimeNotes();
    return true;
  }
  chimePending = true;
  void ctx.resume().then(
    () => {
      if (ctx.state === 'running' && chimePending) {
        chimePending = false;
        playChimeNotes();
      }
    },
    () => undefined,
  );
  return false;
}

let unlockInstalled = false;

/** Resume the audio context on the first user gesture so later chimes can play. */
export function installChimeUnlock(): void {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const unlock = () => {
    const ctx = getContext();
    const ready = ctx && ctx.state === 'suspended' ? ctx.resume() : Promise.resolve();
    void ready.then(
      () => {
        if (chimePending && ctx?.state === 'running') {
          chimePending = false;
          playChimeNotes();
        }
      },
      () => undefined,
    );
  };
  // Stays installed: a chime held while audio was locked still plays on a later click or key press.
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}
